/**
 * ถ้ำคลื่นคำราม (docs/systems/DUNGEON_Y1.md): the first dungeon's rules data. The Wave Mask pirates' sea-cave hideout under
 * the island shrine of Yanyu B, four rooms, for 1–5 players. Data only; systems/dungeon.ts runs it. Its monsters,
 * moves, statuses, drops, gear and the interior are folded into their lists (monsters.ts, skills.ts, status.ts,
 * drops.ts, items.ts, interiors.ts). Names follow docs/LORE.md (`WAVE_MASK` in data/lore.ts).
 */
import type { InteriorDef } from './interiors';
import type { ItemDef } from './items';
import type { MonsterDef } from './monsters';
import type { SkillDef } from './skills';

export const DUNGEON_ID = 'dg-wave-roar';
export const DUNGEON_NAME = 'ถ้ำคลื่นคำราม';

// ───────────────────────── party and scaling ─────────────────────────

export const DUNGEON = {
  minParty: 1,
  maxParty: 5,
  /** School helpers a solo player may take along (they do not count toward the scaling). */
  soloHelpers: 2,
  /** Seconds a boss chest's daily lock lasts: one claim per character per arena day (`progress.arena.day`). */
  bossClaimsPerDay: 1,
  /** The boss's base health over a room-1 grunt's. */
  bossHpMult: 12,
  /** The enrage: at this share of health, for this long. */
  enrageAt: 0.3,
  enrageSeconds: 20,
  /** The mini-boss's decoys come at this share of health, and fade after this long. */
  decoysAt: 0.5,
  decoySeconds: 15,
  /** Room 3's surf lanes: one sweeps every `beat` seconds, shown `warn` seconds before. */
  surfBeat: 4.5,
  surfWarn: 1.5,
  /** A sleeping target wakes when struck; a sleep lasts at most this long. */
  sleepSeconds: 3,
  rootSeconds: 1.5,
} as const;

/** Party size kept to 1–5. */
export const partySize = (n: number): number =>
  Math.max(DUNGEON.minParty, Math.min(DUNGEON.maxParty, Math.floor(n)));
/** Enemy health multiplier for a party of n: ×(0.6 + 0.4 n). */
export const dungeonHpScale = (n: number): number => 0.6 + 0.4 * partySize(n);
/** Enemy damage multiplier for a party of n: ×(0.8 + 0.05 n). */
export const dungeonDmgScale = (n: number): number => 0.8 + 0.05 * partySize(n);
/** Room 1 holds 6 for 1–2 players, 7 for 3–4, 8 for 5. */
export const room1Count = (n: number): number => (partySize(n) >= 5 ? 8 : partySize(n) >= 3 ? 7 : 6);

// ───────────────────────── the map ─────────────────────────

/**
 * The cave as one interior floor (interiors.ts `generateInterior` conventions: O rock, ~ water, X crates, P pillars,
 * L lanterns, G chest, # cell bars, D the way out). 40 × 46, the door at the bottom; rooms run 1 (south) → 2 (west)
 * → 3 (east) → 4 (north). Coordinates below are (column, row) in this plan.
 */
export const DUNGEON_PLAN: readonly string[] = (() => {
  const W = 40;
  const H = 46;
  const g = Array.from({ length: H }, () => Array.from({ length: W }, () => 'O'));
  const fill = (x0: number, x1: number, y0: number, y1: number, ch: string) => {
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) g[y]![x] = ch;
  };
  fill(8, 33, 1, 11, '.'); // 4 vault
  fill(26, 28, 12, 14, '.'); // 3 → 4
  fill(18, 37, 15, 28, '.'); // 3 drill yard
  fill(13, 17, 21, 23, '.'); // 2 → 3
  fill(1, 12, 15, 30, '.'); // 2 sea cells
  fill(4, 6, 31, 32, '.'); // 1 → 2
  fill(1, 38, 33, 44, '.'); // 1 hidden dock
  fill(31, 38, 34, 44, '~'); // the dock's inlet
  fill(31, 36, 39, 39, '.'); // its pier
  for (const y of [16, 18, 20, 22, 24, 26]) g[y]![4] = '#'; // the cells' bars (a gap at each villager's row)
  for (const y of [19, 23]) for (let x = 1; x <= 3; x++) g[y]![x] = '#';
  for (const [x, y] of [
    [12, 3],
    [29, 3],
    [12, 9],
    [29, 9],
  ] as const)
    g[y]![x] = 'P';
  for (const [x, y] of [
    [8, 1],
    [33, 1],
    [1, 33],
    [18, 15],
    [37, 15],
  ] as const)
    g[y]![x] = 'L';
  for (const [x, y] of [
    [20, 1],
    [21, 1],
  ] as const)
    g[y]![x] = 'G';
  for (const [x, y] of [
    [9, 10],
    [10, 10],
    [32, 10],
    [31, 10],
  ] as const)
    g[y]![x] = '~';
  for (const [x, y] of [
    [3, 41],
    [4, 41],
    [3, 42],
    [27, 35],
    [28, 35],
    [22, 43],
  ] as const)
    g[y]![x] = 'X';
  g[45]![19] = 'D';
  return g.map((r) => r.join(''));
})();

export type PlanRect = readonly [x0: number, x1: number, y0: number, y1: number];
export type PlanPt = readonly [x: number, y: number];

export interface DungeonSpawn {
  readonly monster: string;
  readonly at: PlanPt;
  /** Only with a party of at least this many. */
  readonly minParty?: number;
}

export interface DungeonRoom {
  readonly n: 1 | 2 | 3 | 4;
  readonly name: string;
  readonly rect: PlanRect;
  readonly spawns: readonly DungeonSpawn[];
  /** The passage out of this room toward the next: barred until every enemy here is down. */
  readonly gate?: PlanRect;
}

export const DUNGEON_ROOMS: readonly DungeonRoom[] = [
  {
    n: 1,
    name: 'ท่าเรือลับ',
    rect: [1, 38, 33, 44],
    gate: [4, 6, 31, 32],
    spawns: [
      { monster: 'dg-grunt', at: [8, 37] },
      { monster: 'dg-grunt', at: [12, 41] },
      { monster: 'dg-grunt', at: [20, 36] },
      { monster: 'dg-harpooner', at: [24, 40] },
      { monster: 'dg-harpooner', at: [16, 35] },
      { monster: 'dg-doctor', at: [10, 43] },
      { monster: 'dg-grunt', at: [26, 37], minParty: 3 },
      { monster: 'dg-executioner', at: [14, 39], minParty: 5 },
    ],
  },
  {
    n: 2,
    name: 'คุกใต้น้ำ',
    rect: [1, 12, 15, 30],
    gate: [13, 17, 21, 23],
    spawns: [
      { monster: 'dg-jailer', at: [8, 17] },
      { monster: 'dg-jailer', at: [8, 23] },
      { monster: 'dg-jailer', at: [8, 28] },
      { monster: 'dg-executioner', at: [10, 20] },
      { monster: 'dg-doctor', at: [11, 26] },
    ],
  },
  {
    n: 3,
    name: 'ลานฝึกน้ำวน',
    rect: [18, 37, 15, 28],
    gate: [26, 28, 12, 14],
    spawns: [
      { monster: 'dg-silver-wave', at: [28, 21] },
      { monster: 'dg-guard', at: [24, 19] },
      { monster: 'dg-guard', at: [32, 23] },
    ],
  },
  { n: 4, name: 'ห้องสมบัติเรือจม', rect: [8, 33, 1, 11], spawns: [{ monster: 'dg-sha-tian', at: [20, 5] }] },
];

/** The kidnapped villagers in the cells (optional goal): freed by talking to them once room 2's guards are down. */
export const DUNGEON_VILLAGERS: readonly {
  readonly name: string;
  readonly sprite: string;
  readonly at: PlanPt;
}[] = [
  { name: 'ชาวประมงที่ถูกจับ', sprite: 'fisherOld', at: [2, 17] },
  { name: 'แม่ค้าที่ถูกจับ', sprite: 'farmerAunt', at: [2, 21] },
  { name: 'เด็กที่ถูกจับ', sprite: 'townKid', at: [2, 25] },
];
/** Room 3's surf lanes, swept west to east. */
export const SURF_LANES: readonly PlanRect[] = [
  [18, 37, 16, 17],
  [18, 37, 19, 20],
  [18, 37, 22, 23],
  [18, 37, 25, 26],
];
/** Who offers the school helpers (by the door), and where the shortcut out opens once the boss falls. */
export const DUNGEON_HELPER_NPC = {
  name: 'ศิษย์ส่งข่าว',
  title: 'ผู้ช่วยจากสำนัก',
  sprite: 'watchman',
  at: [21, 43] as PlanPt,
};
export const DUNGEON_SHORTCUT: PlanPt = [20, 10];
/** The entrance on Yanyu B: a cave mouth under the island shrine's cliff (docs/handoff/newdir/DUNGEON-hooks.patch). */
export const DUNGEON_ENTRANCE = {
  zone: 'yanyu2',
  building: 'yanyu2-sea-cave',
  rect: [398, 406, 384, 392],
  door: [402, 391, 's'],
} as const;

// ───────────────────────── enemies ─────────────────────────

const pirate = (
  o: Partial<MonsterDef> & Pick<MonsterDef, 'id' | 'name' | 'hp' | 'atk' | 'attack'>,
): MonsterDef => ({
  level: 11,
  def: 16,
  speed: 2.3,
  aggro: 5,
  leash: 14,
  decisionRate: 1.2,
  canBlock: true,
  reward: { cultivation: 40, gold: [14, 26] },
  respawn: null,
  sprite: 'bandit',
  scale: 1,
  ...o,
});

/** A room-1 grunt's health: the boss has `DUNGEON.bossHpMult` times this. */
export const GRUNT_HP = 500;

export const DUNGEON_MONSTERS: readonly MonsterDef[] = [
  pirate({
    id: 'dg-grunt',
    name: 'สมุนหน้ากากคลื่น',
    hp: GRUNT_HP,
    atk: 24,
    attack: 'dg-wave-cut',
    break: 'dg-kick',
  }),
  pirate({
    id: 'dg-guard',
    name: 'องครักษ์หน้ากากคลื่น',
    hp: 650,
    atk: 26,
    def: 20,
    attack: 'dg-wave-cut',
    break: 'dg-kick',
  }),
  pirate({
    id: 'dg-harpooner',
    name: 'มือฉมวก',
    hp: 380,
    atk: 22,
    def: 10,
    canBlock: false,
    attack: 'dg-harpoon-throw',
    break: 'dg-harpoon-hook',
    sprite: 'knifer',
  }),
  pirate({
    id: 'dg-doctor',
    name: 'หมอผงทะเล',
    hp: 360,
    atk: 18,
    def: 10,
    canBlock: false,
    attack: 'dg-powder-dart',
    sprite: 'assassin',
  }),
  pirate({
    id: 'dg-jailer',
    name: 'ผู้คุมโซ่เหล็ก',
    hp: 560,
    atk: 23,
    def: 18,
    attack: 'dg-chain-lash',
    break: 'dg-dragon-chain',
    sprite: 'rogueMonk',
  }),
  pirate({
    id: 'dg-executioner',
    name: 'เพชฌฆาตกระบองคู่',
    hp: 720,
    atk: 30,
    def: 20,
    speed: 1.9,
    decisionRate: 1.5,
    canBlock: false,
    attack: 'dg-club-sweep',
    break: 'dg-twin-club',
    scale: 1.15,
  }),
  pirate({
    id: 'dg-silver-wave',
    name: 'เจ้าคลื่นเงิน',
    level: 12,
    hp: 2500,
    atk: 34,
    def: 20,
    speed: 3,
    aggro: 6,
    decisionRate: 1,
    attack: 'dg-dagger-flurry',
    break: 'dg-dagger-break',
    reward: { cultivation: 120, gold: [60, 90] },
    sprite: 'assassin',
  }),
  // The decoys of ร่างแยกเงาจันทร์: one blow and they burst into spray.
  pirate({
    id: 'dg-moon-shadow',
    name: 'เจ้าคลื่นเงิน',
    level: 12,
    hp: 1,
    atk: 6,
    def: 0,
    canBlock: false,
    attack: 'dg-dagger-flurry',
    reward: { cultivation: 0, gold: [0, 0] },
    sprite: 'assassin',
  }),
  pirate({
    id: 'dg-sha-tian',
    name: 'ฉลามเหล็ก ซาเทียน',
    level: 14,
    hp: GRUNT_HP * 12,
    atk: 105,
    def: 26,
    speed: 2.4,
    aggro: 7,
    leash: 16,
    decisionRate: 1.3,
    attack: 'dg-shark-slash',
    break: 'dg-shark-crush',
    // The chest pays the big reward (once a day); the kill itself pays like a grunt.
    reward: { cultivation: 40, gold: [20, 30] },
    sprite: 'worldBoss',
    scale: 1.6,
  }),
];

// ───────────────────────── moves ─────────────────────────

/**
 * How a move is resolved beyond the plain skill system: `scripted` moves only wind up in the World and the dungeon
 * system resolves their shape (cone, ring, line, pull…); `armour` moves cannot be interrupted or stunned out of.
 */
export interface DungeonMoveRule {
  readonly scripted?: 'cone' | 'ring' | 'line' | 'pull' | 'dash3' | 'heal' | 'decoys' | 'reflect' | 'enrage';
  readonly armour?: boolean;
  /** Shape sizes in metres: radius (cone, ring, pull, burst), half-angle in degrees (cone), length and half-width (line). */
  readonly r?: number;
  readonly angle?: number;
  readonly len?: number;
  readonly half?: number;
  /** What answers it (for the docs, the tests and the art lane). */
  readonly answer: string;
  readonly knockback?: number;
  /** A break (破) landing during the wind-up cancels it. */
  readonly breakCancels?: boolean;
  /** Blocking keeps off this (its stun only, for the palm). */
  readonly guardStopsStun?: boolean;
}

const move = (o: Omit<SkillDef, 'qi' | 'desc'> & { desc?: string }): SkillDef => ({ qi: 0, desc: '', ...o });

const BASE_SKILLS: readonly SkillDef[] = [
  move({
    id: 'dg-wave-cut',
    name: 'ดาบคลื่นซัด',
    glyph: '斬',
    kind: 'attack',
    cooldown: 0,
    windup: 0.5,
    range: 2.0,
    power: 0.7,
    hits: 2,
    fx: 'sabre',
  }),
  move({
    id: 'dg-kick',
    name: 'ถีบกระแทก',
    glyph: '破',
    kind: 'break',
    cooldown: 5,
    windup: 0.8,
    range: 2.0,
    power: 1.2,
    stun: 1.4,
    fx: 'crush',
  }),
  move({
    id: 'dg-harpoon-throw',
    name: 'ซัดฉมวก',
    glyph: '刃',
    kind: 'attack',
    cooldown: 0,
    windup: 0.6,
    range: 6,
    power: 0.6,
    volley: 1,
    fx: 'pierce',
  }),
  move({
    id: 'dg-harpoon-hook',
    name: 'ฉมวกเกี่ยววิญญาณ',
    glyph: '破',
    kind: 'break',
    cooldown: 7,
    windup: 0.9,
    range: 7,
    power: 0.9,
    stun: 1,
    fx: 'dgHarpoon',
  }),
  move({
    id: 'dg-backstep',
    name: 'ถอยหลบ',
    glyph: '疾',
    kind: 'buff',
    cooldown: 4,
    windup: 0,
    range: 0,
    power: 0,
    fx: 'wind',
  }),
  move({
    id: 'dg-powder-dart',
    name: 'เป่าลูกดอก',
    glyph: '刺',
    kind: 'attack',
    cooldown: 0,
    windup: 0.5,
    range: 5,
    power: 0.6,
    volley: 1,
    fx: 'needles',
  }),
  move({
    id: 'dg-sleep-powder',
    name: 'เป่าผงหลับใหล',
    glyph: '眠',
    kind: 'attack',
    cooldown: 10,
    windup: 1.0,
    range: 4.5,
    power: 0.2,
    fx: 'dgSleep',
  }),
  move({
    id: 'dg-salt-salve',
    name: 'ยาทาแผลน้ำเค็ม',
    glyph: '藥',
    kind: 'heal',
    cooldown: 12,
    windup: 1.2,
    range: 7,
    power: 0,
    healPct: 0.25,
    fx: 'calm',
  }),
  move({
    id: 'dg-chain-lash',
    name: 'ฟาดโซ่',
    glyph: '鎖',
    kind: 'attack',
    cooldown: 0,
    windup: 0.55,
    range: 3,
    power: 0.9,
    fx: 'chain',
  }),
  move({
    id: 'dg-dragon-chain',
    name: 'โซ่พันมังกร',
    glyph: '縛',
    kind: 'break',
    cooldown: 8,
    windup: 0.8,
    range: 6,
    power: 0.8,
    fx: 'dgChain',
  }),
  move({
    id: 'dg-chain-spin',
    name: 'หมุนโซ่ป้องกาย',
    glyph: '守',
    kind: 'buff',
    cooldown: 10,
    windup: 0.3,
    range: 0,
    power: 0.6,
    fx: 'whirl',
  }),
  move({
    id: 'dg-club-sweep',
    name: 'กวาดกระบอง',
    glyph: '棍',
    kind: 'attack',
    cooldown: 0,
    windup: 0.6,
    range: 2.3,
    power: 1,
    fx: 'crush',
  }),
  move({
    id: 'dg-twin-club',
    name: 'กระบองคู่ทุบหิน',
    glyph: '破',
    kind: 'break',
    cooldown: 7,
    windup: 1.6,
    range: 2.4,
    power: 2.2,
    stun: 2,
    fx: 'quake',
  }),
  move({
    id: 'dg-dagger-flurry',
    name: 'มีดคู่ระลอกคลื่น',
    glyph: '刺',
    kind: 'attack',
    cooldown: 0,
    windup: 0.4,
    range: 2,
    power: 0.55,
    hits: 3,
    fx: 'slash',
  }),
  move({
    id: 'dg-dagger-break',
    name: 'มีดคู่ไขว้ทะลวง',
    glyph: '破',
    kind: 'break',
    cooldown: 6,
    windup: 0.8,
    range: 2.1,
    power: 1.3,
    stun: 1.5,
    fx: 'crush',
  }),
  move({
    id: 'dg-triple-wave',
    name: 'เงาคลื่นสามสาย',
    glyph: '影',
    kind: 'attack',
    cooldown: 11,
    windup: 0.6,
    range: 6,
    power: 0.8,
    fx: 'shadow',
  }),
  move({
    id: 'dg-frost-cut',
    name: 'ลมปราณน้ำแข็งกรีด',
    glyph: '寒',
    kind: 'attack',
    cooldown: 8,
    windup: 0.5,
    range: 2.2,
    power: 1,
    applies: { status: 'chill' },
    fx: 'icebreak',
  }),
  move({
    id: 'dg-moon-decoys',
    name: 'ร่างแยกเงาจันทร์',
    glyph: '月',
    kind: 'buff',
    cooldown: 999,
    windup: 0.8,
    range: 0,
    power: 0,
    fx: 'shadow',
  }),
  move({
    id: 'dg-surf-lane',
    name: 'คลื่นซัดลาน',
    glyph: '浪',
    kind: 'power',
    cooldown: 0,
    windup: 0,
    range: 20,
    power: 0.9,
    fx: 'wind',
  }),
  move({
    id: 'dg-shark-slash',
    name: 'ดาบฉลามฟัน',
    glyph: '斬',
    kind: 'attack',
    cooldown: 0,
    windup: 0.55,
    range: 2.6,
    power: 0.6,
    fx: 'sabre',
  }),
  move({
    id: 'dg-shark-crush',
    name: 'ดาบฉลามทุบเกราะ',
    glyph: '破',
    kind: 'break',
    cooldown: 6,
    windup: 0.9,
    range: 2.6,
    power: 0.9,
    stun: 1.5,
    fx: 'cleave',
  }),
  move({
    id: 'dg-giant-palm',
    name: 'ฝ่ามือคลื่นยักษ์',
    glyph: '掌',
    kind: 'power',
    cooldown: 9,
    windup: 1.4,
    range: 5,
    power: 1.5,
    fx: 'palm',
  }),
  move({
    id: 'dg-sea-turn',
    name: 'ดาบฉลามพลิกสมุทร',
    glyph: '旋',
    kind: 'power',
    cooldown: 12,
    windup: 1.0,
    range: 10,
    power: 1.4,
    fx: 'ring',
  }),
  move({
    id: 'dg-whirlpool',
    name: 'วังน้ำวนดูดวิญญาณ',
    glyph: '渦',
    kind: 'power',
    cooldown: 20,
    windup: 2.0,
    range: 10,
    power: 1.6,
    fx: 'dgWhirlpool',
  }),
  move({
    id: 'dg-sea-split',
    name: 'ฟันแบ่งทะเล',
    glyph: '裂',
    kind: 'power',
    cooldown: 10,
    windup: 1.2,
    range: 11,
    power: 1.6,
    fx: 'cleave',
  }),
  move({
    id: 'dg-iron-body',
    name: 'ร่างเหล็กใต้สมุทร',
    glyph: '鐵',
    kind: 'buff',
    cooldown: 999,
    windup: 0.8,
    range: 0,
    power: 0,
    fx: 'dgIron',
  }),
];

export const DUNGEON_MOVE_RULES: Readonly<Record<string, DungeonMoveRule>> = {
  'dg-harpoon-hook': {
    scripted: 'line',
    len: 7,
    half: 0.6,
    answer: 'หลบด้วยแดช/วิชาตัวเบา หรือออกนอกแนว (การ์ดไม่ช่วย: 破)',
  },
  'dg-backstep': { scripted: 'dash3', answer: 'ถอยห่างเมื่อถูกประชิด (ระยะ < 2.6 ม.)' },
  'dg-sleep-powder': {
    scripted: 'cone',
    r: 4.5,
    angle: 35,
    answer: 'ตั้งรับ (攻) หรือออกนอกกรวย; ไข่มุกสงบคลื่นกันได้; ถูกตีแล้วตื่น',
  },
  'dg-salt-salve': { scripted: 'heal', answer: 'ขัดด้วยการตี (stun) หรือฆ่าหมอก่อน' },
  'dg-dragon-chain': { scripted: 'line', len: 6, half: 0.6, answer: 'ออกนอกแนว; โดนแล้วติดราก 1.5 วินาที' },
  'dg-chain-spin': { scripted: 'reflect', answer: 'อย่ายิงไกลใส่; ใช้ท่าทำลาย (破) ทำลายการตั้งรับ' },
  'dg-twin-club': { armour: true, answer: 'ถอยออกนอกระยะ (ขัดไม่ได้ เกราะขณะเหวี่ยง)' },
  'dg-triple-wave': {
    scripted: 'dash3',
    len: 5,
    half: 0.9,
    answer: 'หลบออกจากแนวพุ่ง; เงาน้ำที่ทิ้งไว้ทำให้ช้า',
  },
  'dg-moon-decoys': { scripted: 'decoys', answer: 'ตามรอยเท้า: ตัวจริงมีรอยเท้าน้ำ เงาแตกในทีเดียว' },
  'dg-surf-lane': { scripted: 'line', answer: 'ออกจากแถบคลื่นก่อนซัด หรือกระโดดข้าม' },
  'dg-giant-palm': {
    scripted: 'cone',
    r: 5,
    angle: 40,
    armour: true,
    knockback: 2.5,
    guardStopsStun: true,
    answer: 'ก้าวออกนอกกรวย หรือตั้งรับ (กันได้แค่มึน)',
  },
  'dg-sea-turn': { scripted: 'ring', r: 3.6, armour: true, answer: 'แดชหรือวิชาตัวเบาออกนอกวง' },
  'dg-whirlpool': {
    scripted: 'pull',
    r: 10,
    armour: true,
    breakCancels: true,
    answer: 'ใช้ท่าทำลาย (破) ใส่ระหว่างร่ายเพื่อยกเลิก หรือกระโดดลอยหนีแรงดูด',
  },
  'dg-sea-split': { scripted: 'line', len: 11, half: 0.9, armour: true, answer: 'ก้าวหลบออกด้านข้างของเส้น' },
  'dg-iron-body': {
    scripted: 'enrage',
    answer: 'ทุ่มความเสียหายใส่ หรือวิ่งหนีจนหมด 20 วินาที (สมุนสองคนกระโดดเข้ามา)',
  },
};

/** The moves, with `scripted` and `armour` set from their rules (the World reads those two). */
export const DUNGEON_SKILLS: readonly SkillDef[] = BASE_SKILLS.map((s) => {
  const r = DUNGEON_MOVE_RULES[s.id];
  return { ...s, ...(r?.scripted ? { scripted: true } : {}), ...(r?.armour ? { armour: true } : {}) };
});

/** Statuses the dungeon adds (to fold into status.ts `list`). */
export const DUNGEON_STATUSES = [
  {
    id: 'dg-sleep',
    name: 'หลับใหล',
    glyph: '眠',
    kind: 'debuff',
    maxStacks: 1,
    duration: 3,
    desc: 'หลับไปเพราะผงทะเล ถูกตีแล้วตื่น',
  },
  {
    id: 'dg-root',
    name: 'โซ่พันกาย',
    glyph: '縛',
    kind: 'debuff',
    maxStacks: 1,
    duration: 1.5,
    speedMul: 0.01,
    noQinggong: true,
    desc: 'ถูกโซ่พันขาไว้ ขยับไม่ได้',
  },
  {
    id: 'dg-iron-skin',
    name: 'ร่างเหล็กใต้สมุทร',
    glyph: '鐵',
    kind: 'buff',
    maxStacks: 1,
    duration: 20,
    damageTakenMul: 0.6,
    damageDealtMul: 1.25,
    desc: 'หนังดุจเหล็ก รับความเสียหายลดลง 40% โจมตีแรงขึ้น 25%',
  },
] as const;

// ───────────────────────── rewards ─────────────────────────

/** Set ชุดคลื่นคำราม and the boss chest's extras (ITEMS' slots; folded into items.ts by DUNGEON-hooks.patch). */
export const DUNGEON_SET = ['dg-set-head', 'dg-set-body', 'dg-set-legs', 'dg-set-feet'] as const;
export const BOSS_CHEST = {
  /** One set piece every claim. */
  set: 1,
  pearl: { item: 'dg-calm-pearl', chance: 0.15 },
  manual: { item: 'dg-manual-wave-cut', chance: 0.15 },
  /** Each villager freed adds this to the chest. */
  perVillager: { chance: 0.05, gold: 500, cultivation: 100 },
  gold: 1500,
  cultivation: 400,
} as const;

/** Drop tables for the dungeon's people (to fold into drops.ts `DROP_TABLES`): existing goods and gear only. */
export const DUNGEON_DROPS = {
  'dg-grunt': [
    { good: 'rice', chance: 0.3, n: [1, 2] },
    { item: 'straw-sandals', chance: 0.05 },
  ],
  'dg-guard': [{ item: 'cotton-trousers', chance: 0.08 }],
  'dg-harpooner': [{ item: 'iron-darts', chance: 0.08 }],
  'dg-doctor': [{ good: 'herb-leaf', chance: 0.5, n: [1, 3] }],
  'dg-jailer': [{ item: 'wolf-greaves', chance: 0.04 }],
  'dg-executioner': [{ item: 'cloud-boots', chance: 0.04 }],
  'dg-silver-wave': [
    { item: 'plum-needles', chance: 0.25 },
    { item: 'cloud-boots', chance: 0.15 },
  ],
} as const;

/** The set pieces and the chest's extras (folded into items.ts). Stats only: the character models are final. */
export const DUNGEON_GEAR: readonly ItemDef[] = [
  {
    id: 'dg-set-head',
    name: 'ผ้าโพกคลื่นคำราม',
    slot: 'head',
    rarity: 'epic',
    desc: 'ผ้าโพกสีครามขลิบทอง ยึดจากคลังของฉลามเหล็ก ชุดคลื่นคำราม (1/4)',
    stats: { def: 4, maxHp: 30 },
    visual: '',
    worth: 900,
  },
  {
    id: 'dg-set-body',
    name: 'เสื้อคลุมคลื่นคำราม',
    slot: 'body',
    rarity: 'epic',
    desc: 'เสื้อคลุมหนังปลาฉลามย้อมคราม ทนคมดาบและน้ำเค็ม ชุดคลื่นคำราม (2/4)',
    stats: { def: 7, maxHp: 50 },
    visual: '',
    worth: 1200,
  },
  {
    id: 'dg-set-legs',
    name: 'กางเกงคลื่นคำราม',
    slot: 'legs',
    rarity: 'epic',
    desc: 'กางเกงรัดแข้งของพลเรือโจรสลัด ลุยน้ำตื้นได้ไม่เปียก ชุดคลื่นคำราม (3/4)',
    stats: { def: 5, maxHp: 30 },
    visual: '',
    worth: 900,
  },
  {
    id: 'dg-set-feet',
    name: 'รองเท้าคลื่นคำราม',
    slot: 'feet',
    rarity: 'epic',
    desc: 'รองเท้าพื้นเชือกถักกันลื่นบนดาดฟ้าเรือ ชุดคลื่นคำราม (4/4)',
    stats: { def: 2, speed: 0.04 },
    visual: '',
    worth: 900,
  },
  {
    id: 'dg-calm-pearl',
    name: 'ไข่มุกสงบคลื่น',
    slot: 'treasure',
    rarity: 'legend',
    desc: 'ไข่มุกเย็นเฉียบจากก้นอ่าว ผู้พกไว้ไม่หลับเพราะผงทะเล',
    stats: { maxHp: 40 },
    visual: '',
    worth: 1500,
  },
  {
    id: 'dg-manual-wave-cut',
    name: 'ตำราดาบคลื่นซัด',
    slot: 'manual',
    rarity: 'epic',
    desc: 'ตำราดาบของโจรสลัดหน้ากากคลื่น จดท่าฟันตามจังหวะคลื่น อ่านแล้วกระบวนท่าของดาบขอเกี่ยวโคมแรงขึ้น 12%',
    stats: {},
    art: { id: 'lamp-hook-sabre', pct: 0.12 },
    visual: '',
    worth: 800,
  },
];

/** The cave as an interior (data/interiors.ts lists it; its door is `DUNGEON_LINK`). */
export const DUNGEON_INTERIOR: InteriorDef = {
  id: DUNGEON_ID,
  name: DUNGEON_NAME,
  zone: 'yanyu2',
  template: 'cave',
  seed: 77,
  rules: { combat: true, safe: false, jump: true, sprint: true },
  floors: [{ level: 0, name: DUNGEON_NAME, plan: DUNGEON_PLAN, floor: 'earth', wall: 'rock', dark: 0.7 }],
};
export const DUNGEON_LINK = {
  zone: 'yanyu2',
  building: DUNGEON_ENTRANCE.building,
  interior: DUNGEON_ID,
} as const;

/** The school helpers a solo player may take: two of the player's school (its uniform and first two arts). */
export const HELPER_NAMES: readonly [string, string] = ['ศิษย์พี่', 'ศิษย์น้อง'];
/** Without a school, two wanderers instead. */
export const WANDERER_HELPERS: readonly {
  readonly name: string;
  readonly sprite: string;
  readonly art: string;
}[] = [
  { name: 'ดาบพเนจรหาญ', sprite: 'heroBlood', art: 'heat-sabre' },
  { name: 'หมัดพเนจรสงบ', sprite: 'heroShaolin', art: 'bell-fist' },
];

/** What a character keeps of the dungeon: the day of the last boss claim (`progress.arena.day`) and clears. */
export interface DungeonSave {
  day: string;
  claimed: number;
  clears: number;
}
export const newDungeonSave = (): DungeonSave => ({ day: '', claimed: 0, clears: 0 });
export function parseDungeonSave(raw: unknown): DungeonSave {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<DungeonSave>;
  const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.floor(v)) : 0);
  return { day: typeof r.day === 'string' ? r.day : '', claimed: n(r.claimed), clears: n(r.clears) };
}
